import { useEffect, useRef, useCallback } from 'react';

const HEARTBEAT_INTERVAL = 25000;
const MAX_RETRIES = 3;

export function useQuizWebSocket(url, onMessage, enabled = true) {
    const wsRef        = useRef(null);
    const heartbeatRef = useRef(null);
    // onMessage may change on each render; keep a stable ref so the WS closure always
    // calls the latest version without becoming a dependency of the effect.
    const onMessageRef = useRef(onMessage);
    useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

    const clearHeartbeat = useCallback(() => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!url || !enabled) return;

        // Each effect instance owns an `active` flag.
        // Setting it to false on cleanup prevents stale retries from ever firing.
        let active = true;
        let retries = 0;

        function connect() {
            if (!active) return;

            const ws = new WebSocket(url);
            wsRef.current = ws;

            ws.onopen = () => {
                retries = 0;
                heartbeatRef.current = setInterval(() => {
                    if (ws.readyState === WebSocket.OPEN) ws.send('ping');
                }, HEARTBEAT_INTERVAL);
            };

            ws.onmessage = (event) => {
                if (event.data === 'pong') return;
                try {
                    const msg = JSON.parse(event.data);
                    if (active) onMessageRef.current(msg);
                } catch {
                    // ignore malformed frames
                }
            };

            ws.onclose = () => {
                clearHeartbeat();
                if (!active) return;          // effect was cleaned up — drop retry
                if (retries < MAX_RETRIES) {
                    retries += 1;
                    const delay = Math.min(1000 * Math.pow(2, retries - 1), 8000);
                    setTimeout(connect, delay);
                }
            };

            ws.onerror = () => ws.close();
        }

        connect();

        return () => {
            active = false;           // cancels any pending retry for this URL
            clearHeartbeat();
            if (wsRef.current) wsRef.current.close();
        };
    // Re-run (and thus cancel old retries) whenever URL or enabled changes
    }, [url, enabled, clearHeartbeat]);
}
